import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import CarCard from '../../features/CarCard';
import { IconSearch } from '../../ui/Icons';
import { getFilterOptions, filterVehicles } from '../../../data/vehicles';
import { useVehicles } from '../../../context/VehiclesContext';
import { makeFromSlug, buildSearchUrl, filtersFromSearchParams } from '../../../utils/urlHelpers';
import './SearchResults.css';

const EMPTY_FILTERS = { make: '', bodyType: '', year: '', price: '', transmission: '', fuel: '' };

const SearchResultsPage = ({ onViewDetails }) => {
  const { vehicles: allVehicles } = useVehicles();
  const { make: makeSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('default');

  const filterOptions = useMemo(() => getFilterOptions(allVehicles), [allVehicles]);

  // Derive filters entirely from the URL — no duplicate state
  const filters = useMemo(() => {
    if (makeSlug) {
      const make = makeFromSlug(makeSlug, allVehicles);
      return { ...EMPTY_FILTERS, make };
    }
    return filtersFromSearchParams(searchParams);
  }, [makeSlug, searchParams, allVehicles]);

  const results = useMemo(() => {
    let filtered = filterVehicles(allVehicles, filters);
    if (sortBy === 'price-low')    filtered = [...filtered].sort((a, b) => a.price - b.price);
    else if (sortBy === 'price-high') filtered = [...filtered].sort((a, b) => b.price - a.price);
    else if (sortBy === 'year-new')   filtered = [...filtered].sort((a, b) => b.year - a.year);
    else if (sortBy === 'mileage-low') filtered = [...filtered].sort((a, b) => a.mileage - b.mileage);
    return filtered;
  }, [filters, sortBy]);

  const activeFilterCount = Object.values(filters).filter((v) => v !== '').length;

  // Update page title
  useEffect(() => {
    if (filters.make && activeFilterCount === 1) {
      document.title = `${filters.make} Cars for Sale | JAC Cars`;
    } else {
      document.title = 'Search Results | JAC Cars';
    }
  }, [filters, activeFilterCount]);

  // When a filter dropdown changes, push the new URL (browser history entry)
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    navigate(buildSearchUrl(newFilters), { replace: false });
  };

  const clearFilters = () => {
    navigate('/inventory');
  };

  const heroLabel = filters.make && activeFilterCount === 1
    ? `${filters.make} Cars`
    : activeFilterCount > 0
    ? 'Search Results'
    : 'All Cars';

  return (
    <div className="search-results-page">
      <div className="search-results-hero">
        <div className="container">
          <h1>{heroLabel}</h1>
          <p>Find your perfect vehicle from our collection</p>
        </div>
      </div>

      <div className="container">
        <div className="search-results-layout">
          <aside className="filters-sidebar">
            <div className="filters-header">
              <h3>Filters</h3>
              {activeFilterCount > 0 && (
                <button className="clear-filters" onClick={clearFilters}>
                  Clear All ({activeFilterCount})
                </button>
              )}
            </div>

            <div className="filter-group">
              <label htmlFor="make">Make</label>
              <select id="make" name="make" value={filters.make} onChange={handleFilterChange}>
                <option value="">All Makes</option>
                {filterOptions.makes.map((make) => (
                  <option key={make} value={make}>{make}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="bodyType">Body Type</label>
              <select id="bodyType" name="bodyType" value={filters.bodyType} onChange={handleFilterChange}>
                <option value="">All Types</option>
                {filterOptions.bodyTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="year">Year</label>
              <select id="year" name="year" value={filters.year} onChange={handleFilterChange}>
                <option value="">All Years</option>
                {filterOptions.years.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="price">Price Range</label>
              <select id="price" name="price" value={filters.price} onChange={handleFilterChange}>
                <option value="">Any Price</option>
                {filterOptions.priceRanges.map((range) => (
                  <option key={range.value} value={range.value}>{range.label}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="transmission">Transmission</label>
              <select id="transmission" name="transmission" value={filters.transmission} onChange={handleFilterChange}>
                <option value="">All Transmissions</option>
                {filterOptions.transmissions.map((trans) => (
                  <option key={trans} value={trans}>{trans}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="fuel">Fuel Type</label>
              <select id="fuel" name="fuel" value={filters.fuel} onChange={handleFilterChange}>
                <option value="">All Fuel Types</option>
                {filterOptions.fuelTypes.map((fuel) => (
                  <option key={fuel} value={fuel}>{fuel}</option>
                ))}
              </select>
            </div>
          </aside>

          <main className="results-main">
            <div className="results-header">
              <span className="results-count">
                {results.length} vehicle{results.length !== 1 ? 's' : ''} found
              </span>
              <div className="sort-container">
                <label htmlFor="sortBy">Sort by:</label>
                <select id="sortBy" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="default">Featured</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="year-new">Year: Newest First</option>
                  <option value="mileage-low">Mileage: Low to High</option>
                </select>
              </div>
            </div>

            {results.length > 0 ? (
              <div className="results-grid">
                {results.map((car) => (
                  <CarCard
                    key={car.id}
                    {...car}
                    onViewDetails={() => onViewDetails && onViewDetails(car)}
                  />
                ))}
              </div>
            ) : (
              <div className="no-results">
                <div className="no-results-icon"><IconSearch /></div>
                <h3>No vehicles found</h3>
                <p>Try adjusting your filters to see more results</p>
                <button className="btn btn-primary" onClick={clearFilters}>
                  Clear All Filters
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default SearchResultsPage;
